import { faCaretLeft, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Stack,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Questionary } from "../../models/Questionary";
import { QuestionaryDetail } from "../../models/QuestionaryDetail";
import { QuestionaryQuestion } from "../../models/QuestionaryQuestion";
import { QuestionarySection } from "../../models/QuestionarySection";
import QuestionaryService from "../../services/questionary.service";
import ReportService from "../../services/report.service";
import useUser from "../App/useUser";
import ModalQuestionaryConfirm from "./ModalQuestionaryConfirm";
import "./style.css";
import { useLoading } from "../App/LoadingContext";

const LoadingOverlay = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1200,
      color: "white",
      flexDirection: "column",
    }}
  >
    <Spinner animation="border" role="status" variant="light" className="mb-3">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
    <h4>Exportando reporte...</h4>
    <p>Por favor, espere un momento.</p>
  </div>
);

const renderMultiColumnTable = (detail: QuestionaryDetail) => {
  try {
    const columns = JSON.parse(detail.questionaryAnswer?.answerDesc || "[]");
    const rows = JSON.parse(detail.answerText || "[]");
    if (rows.length === 0)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;

    return (
      <Table striped bordered hover responsive size="sm" className="mt-2">
        <thead className="table-secondary">
          <tr>
            {columns.map((col: string) => (
              <th key={col} className="text-center">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, rowIndex: number) => (
            <tr key={rowIndex} className="align-middle">
              {columns.map((col: string) => (
                <td key={col} className="text-center">
                  {row[`"${col}"`] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  } catch (e) {
    return <p className="text-danger">Error al mostrar datos de tabla.</p>;
  }
};

const renderMatrixTable = (detail: QuestionaryDetail) => {
  try {
    const desc = detail.questionaryAnswer?.answerDesc;
    if (!desc || !desc.includes("}"))
      return <p className="text-muted fst-italic">Sin respuesta.</p>;
    const parts = desc.split("}");
    if (parts.length < 2)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;

    const rowsDef = JSON.parse(parts[0]);
    const colsDef = JSON.parse(parts[1]);

    const answers = detail.answerText
      ? JSON.parse(detail.answerText).map((r: string) => JSON.parse(r))
      : [];
    if (answers.length === 0)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;

    return (
      <Table bordered hover responsive size="sm" className="mt-2">
        <thead className="table-secondary">
          <tr>
            <th style={{ minWidth: "150px" }}></th>
            {colsDef.map((colName: string, colIndex: number) => (
              <th key={colIndex} className="text-center">
                {colName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsDef.map((rowName: string, rowIndex: number) => (
            <tr key={rowIndex} className="align-middle">
              <td className="table-light fw-bold">{rowName}</td>
              {colsDef.map((_: string, colIndex: number) => (
                <td key={colIndex} className="text-center">
                  {answers[rowIndex]?.[colIndex] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  } catch (e) {
    return <p className="text-danger">Error al mostrar datos de matriz.</p>;
  }
};

const QuestionarySummary = () => {
  const { wrapWithLoading } = useLoading();
  const { user } = useUser();
  const [questionaryDetails, setQuestionaryDetails] = useState<
    QuestionaryDetail[]
  >([]);
  const [questionary, setQuestionary] = useState<Questionary>({
    id: -1,
    questionaryTypeId: -1,
    name: "1",
    user: { id: 0, name: "Jhon Doe", userName: "JohnDoe" },
    isCompleted: false,
    email: "",
  });
  const [sections, setSections] = useState<QuestionarySection[]>([]);
  // --- INICIO DEL CAMBIO ---
  // 1. Nuevo estado para guardar TODAS las preguntas del cuestionario
  const [allQuestions, setAllQuestions] = useState<QuestionaryQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // --- FIN DEL CAMBIO ---
  const [validated, setValidated] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const validationErrorTitle: string = "Error";
  const validationErrorMessage: string =
    "Debe contestar todos los comentarios para poder guardar";
  const saveErrorMessage: string =
    "Ocurrió un error al guardar el questionario";
  const locationRouter = useLocation();
  const navigate = useNavigate();
  const [showModalConfirm, setShowModalConfirm] = useState(true);

  const handleAgree = () => {
    setShowModalConfirm(false);
  };

  const questionaryId =
    locationRouter.state?.questionaryId ||
    parseInt(localStorage.getItem("questionaryId") || "-1");

  useEffect(() => {
    // --- INICIO DEL CAMBIO ---
    // 2. Lógica de carga refactorizada
    async function loadInitialData() {
      if (!questionaryId || questionaryId === -1) return;

      setIsLoading(true);
      try {
        const localQuestionary = await QuestionaryService.getQuestionaryById(
          questionaryId
        );
        setQuestionary(localQuestionary);

        const sectionsData = await QuestionaryService.getSections(
          localQuestionary.questionaryTypeId
        );

        sectionsData.sort((a, b) => a.position - b.position);
        sectionsData.forEach((section) => {
          if (section.questionarySubsections) {
            section.questionarySubsections.sort(
              (a, b) => a.position - b.position
            );
          }
        });

        setSections(sectionsData);

        // Carga todas las preguntas y los detalles en paralelo
        const [questionsData, detailsData] = await Promise.all([
          QuestionaryService.getAllQuestionsForType(
            localQuestionary.questionaryTypeId
          ),
          QuestionaryService.getQuestionaryDetail(questionaryId),
        ]);

        setAllQuestions(questionsData);
        setQuestionaryDetails(detailsData);
      } catch (error) {
        console.error("Error al cargar datos del resumen:", error);
        setErrorTitle("Error de Carga");
        setErrorMessage("No se pudieron cargar los datos para el resumen.");
        setShowValidationError(true);
      } finally {
        setIsLoading(false);
      }
    }
    loadInitialData();
    // --- FIN DEL CAMBIO ---
  }, [questionaryId, locationRouter.state]);

  function handleCommentChanged(detailId?: number, comment?: string) {
    if (detailId === undefined) return;
    const updatedDetails = questionaryDetails.map((detail) =>
      detail.id === detailId ? { ...detail, comment: comment } : detail
    );
    setQuestionaryDetails(updatedDetails);
  }

  // Reemplaza ÚNICAMENTE tu función handleSubmit. Puedes eliminar onCompleteButton.

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowValidationError(false);
    setShowSavedMessage(false);

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setErrorTitle(validationErrorTitle);
      setErrorMessage(validationErrorMessage);
      setShowValidationError(true);
      window.scrollTo(0, 0);
      return;
    }

    setValidated(true);

    // Función interna que encapsula toda la lógica asíncrona.
    const submissionProcess = async () => {
      try {
        // --- Paso 1: Guardar comentarios (antes en handleSubmit) ---
        const saveResponse = await QuestionaryService.saveComments(
          questionaryDetails
        );

        if (!saveResponse.ok) {
          throw new Error("Error al guardar los comentarios.");
        }

        // --- Paso 2: Cambiar estado (lógica de onCompleteButton integrada aquí) ---
        const completeResponse = await QuestionaryService.changeStatus(
          questionaryId,
          3
        );

        if (!completeResponse.ok) {
          throw new Error("Error al cambiar el estado del cuestionario.");
        }

        // --- Si todo tiene éxito ---
        setValidated(false);
        setShowSavedMessage(true);
        window.scrollTo(0, 0);
      } catch (error) {
        // Un solo catch para cualquier error en el proceso
        console.error("Error durante el proceso de envío:", error);
        setErrorTitle(validationErrorTitle);
        setErrorMessage(saveErrorMessage);
        setShowValidationError(true);
        window.scrollTo(0, 0);
      }
    };

    // Ejecutamos el proceso completo envuelto por el LoadingContext.
    await wrapWithLoading(submissionProcess)();
  };

  const handleDownloadPdf = async (event: any) => {
    try {
      setIsExporting(true);
      const pdfBlob = await ReportService.getReportQuestionaryAsPdf(
        questionaryId
      );
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Cuestionario_${questionary?.name}_${questionaryId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      setErrorTitle(validationErrorTitle);
      setErrorMessage("Error al descargar el PDF");
      setShowValidationError(true);
      window.scrollTo(0, 0);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadExcel = async (event: any) => {
    try {
      setIsExporting(true);
      const excelBlob = await ReportService.getReportQuestionaryAsExcel(
        questionaryId
      );
      const url = URL.createObjectURL(excelBlob);

      // Para forzar la descarga con un nombre de archivo, creamos un enlace temporal
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Cuestionario_${questionary?.name}_${questionaryId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      setErrorTitle(validationErrorTitle);
      setErrorMessage("Error al descargar el Excel");
      setShowValidationError(true);
      window.scrollTo(0, 0);
    } finally {
      setIsExporting(false);
    }
  };

  const PdfIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="#D93025"
      viewBox="0 0 16 16"
    >
      <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
      <path d="M4.603 12.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.658-.877.37-.198.816-.22 1.224-.035.516.232.879.67.968 1.212.094.557.022 1.102-.215 1.622a6.6 6.6 0 0 1-.415 1.096c-.086.196-.12.403-.12.614v.035a.5.5 0 0 1-.416.475c-.23.06-.455.11-.675.118-.174.007-.346.036-.501.076a.5.5 0 0 1-.448-.694c.051-.13.11-.266.19-.402.08-.136.166-.27.258-.404a1.8 1.8 0 0 0 .13-.316c.09-.244.09-.557-.036-.803a.97.97 0 0 0-.134-.338.37.37 0 0 0-.122-.182.5.5 0 0 0-.258-.08H8.57a.5.5 0 0 0-.258.08.37.37 0 0 0-.122.182.97.97 0 0 0-.134.338c-.09.244-.09.557.036.803a1.8 1.8 0 0 0 .13.316c.09.136.175.27.258.404.08.136.14.272.19.402a.5.5 0 0 1-.448.694c-.155-.04-.327-.069-.501-.076-.22-.008-.445-.058-.675-.118a.5.5 0 0 1-.416-.475v-.036c0-.211-.035-.418-.12-.614a6.6 6.6 0 0 1-.415-1.096c-.237-.52-.29-1.065-.215-1.622.09-.542.452-.98.968-1.212.407-.185.854-.164 1.224.035.384.205.583.523.658.877.073.34.04.736-.046 1.136a7.269 7.269 0 0 1-.43 1.295c.952.53 1.798 1.24 2.352 2.038a.5.5 0 0 1-.08.704c-.26.262-.63.435-1.08.556-.34.09-.69.16-1.02.215-.33.055-.654.087-1.007.108l-.353.007a.5.5 0 0 1-.42-.592c.094-.455.335-.886.744-1.267.41-.38.918-.695 1.448-.912a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578c-.03-.052-.06-.101-.09-.148a.5.5 0 0 1 .09-.655c.26-.262.59-.415.98-.482a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578.5.5 0 0 1 .09-.655c.25-.247.52-.4.81-.462a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578z" />
    </svg>
  );

  const ExcelIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="#1D6F42"
      viewBox="0 0 16 16"
    >
      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 1.5v2a.5.5 0 0 0 .5.5h2l-2.5-2.5zM5.884 6.68a.5.5 0 1 0-.768.64L7.349 10l-2.233 2.68a.5.5 0 0 0 .768.64L8 10.781l2.116 2.54a.5.5 0 0 0 .768-.64L8.651 10l2.233-2.68a.5.5 0 0 0-.768-.64L8 9.219l-2.116-2.54z" />
    </svg>
  );

  const formatAnswerText = (detail: QuestionaryDetail) => {
    const questionType = detail.questionaryQuestion?.questionTypeId;
    const answerText = detail.answerText;

    if (questionType === 7 && answerText) {
      // Fecha
      const parts = answerText.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }

    if (questionType === 12 && answerText) {
      // Moneda
      return `$${parseFloat(answerText).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    return (
      answerText || detail.questionaryAnswer?.answerDesc || "Sin respuesta."
    );
  };

  useEffect(() => {
    if (showValidationError || showSavedMessage) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
        setShowSavedMessage(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showValidationError, showSavedMessage]);

  return (
    <>
      <ModalQuestionaryConfirm show={showModalConfirm} onAgree={handleAgree} />
      <div>
        {isExporting && <LoadingOverlay />}
        <div className="notification-container">
          {showValidationError && (
            <Alert
              variant="danger"
              onClose={() => setShowValidationError(false)}
              className="notification-alert"
              dismissible
            >
              <Alert.Heading>{errorTitle}</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          )}

          {showSavedMessage && (
            <Alert
              variant="success"
              onClose={() => setShowSavedMessage(false)}
              className="notification-alert"
              dismissible
            >
              <Alert.Heading>Notificación</Alert.Heading>
              <p>Completaste el cuestionario exitosamente</p>
            </Alert>
          )}
        </div>

        <Container className="pt-3">
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <Card>
                <Card.Header className="card-header-main bg-primary text-white">
                  <Row>
                    <Col className="fw-bold">Resumen</Col>
                    <Col className="text-end">Nombre: {questionary?.name}</Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  {sections.map((section) => (
                    <Card key={section.id.toString()} className="mb-3">
                      <Card.Header className="card-header-section bg-primary text-white">
                        {section.name}
                      </Card.Header>
                      {section.questionarySubsections?.map((subsection) => (
                        <Card.Body key={subsection.id}>
                          <Card.Title>{subsection.name}</Card.Title>
                          {/* --- INICIO DEL CAMBIO --- */}
                          {/* 3. El renderizado ahora se basa en allQuestions, no en los detalles */}
                          {allQuestions
                            .filter(
                              (q) => q.questionarySubsectionId === subsection.id
                            )
                            .sort((a, b) => a.position - b.position)
                            .map((questionInfo) => {
                              const detailsForQuestion =
                                questionaryDetails.filter(
                                  (d) =>
                                    d.questionaryQuestionId === questionInfo.id
                                );

                              return (
                                <div
                                  key={questionInfo.id}
                                  className="mb-3 border-bottom pb-2"
                                >
                                  <Row>
                                    <Col>
                                      <Form.Text as="div">
                                        <strong>
                                          <Badge
                                            bg="primary"
                                            pill
                                            className="me-2"
                                          >
                                            {questionInfo.position}
                                          </Badge>
                                          {questionInfo.questionDesc}
                                        </strong>
                                      </Form.Text>
                                    </Col>
                                  </Row>
                                  {/* Si hay detalles, se muestran. Si no, se muestra "Sin respuesta". */}
                                  {detailsForQuestion.length > 0 ? (
                                    detailsForQuestion.map((detail) => (
                                      <Row
                                        key={detail.id}
                                        className="mt-2 align-items-center"
                                      >
                                        {detail?.questionaryQuestion
                                          ?.questionTypeId === 4 ? (
                                          <>
                                            {renderMultiColumnTable(detail)}
                                            <Row>
                                              <Col></Col>
                                              <Col xs={12} md={6}>
                                                <Form.Control
                                                  type="text"
                                                  placeholder="Agregar comentario..."
                                                  maxLength={500}
                                                  value={detail.comment || ""}
                                                  onChange={(e) => {
                                                    handleCommentChanged(
                                                      detail.id,
                                                      e.target.value
                                                    );
                                                  }}
                                                />
                                              </Col>
                                            </Row>
                                          </>
                                        ) : detail.questionaryQuestion
                                            ?.questionTypeId === 8 ? (
                                          <>
                                            {renderMatrixTable(detail)}
                                            <Row>
                                              <Col></Col>
                                              <Col xs={12} md={6}>
                                                <Form.Control
                                                  type="text"
                                                  placeholder="Agregar comentario..."
                                                  maxLength={500}
                                                  value={detail.comment || ""}
                                                  onChange={(e) => {
                                                    handleCommentChanged(
                                                      detail.id,
                                                      e.target.value
                                                    );
                                                  }}
                                                />
                                              </Col>
                                            </Row>
                                          </>
                                        ) : (
                                          <>
                                            <Col xs={12} md={6}>
                                              <Form.Text
                                                className={
                                                  detail.answerText ||
                                                  detail.questionaryAnswer
                                                    ?.answerDesc
                                                    ? ""
                                                    : "text-muted fst-italic"
                                                }
                                              >
                                                {formatAnswerText(detail)}
                                              </Form.Text>
                                            </Col>
                                            <Col xs={12} md={6}>
                                              <Form.Control
                                                type="text"
                                                placeholder="Agregar comentario..."
                                                maxLength={500}
                                                value={detail.comment || ""}
                                                onChange={(e) => {
                                                  handleCommentChanged(
                                                    detail.id,
                                                    e.target.value
                                                  );
                                                }}
                                              />
                                            </Col>
                                          </>
                                        )}
                                      </Row>
                                    ))
                                  ) : (
                                    <Row className="mt-2">
                                      <Col>
                                        <Form.Text className="text-muted fst-italic">
                                          Sin respuesta.
                                        </Form.Text>
                                      </Col>
                                    </Row>
                                  )}
                                </div>
                              );
                            })}
                          {/* --- FIN DEL CAMBIO --- */}
                        </Card.Body>
                      ))}
                    </Card>
                  ))}
                  <div style={{ height: "5rem" }} />
                </Card.Body>
                <Card.Footer className="text-center fixed-bottom bg-light border-top py-3">
                  <Stack
                    direction="horizontal"
                    gap={3}
                    className="justify-content-center"
                  >
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                      <a>
                        <FontAwesomeIcon
                          icon={faCaretLeft}
                          style={{
                            paddingRight: "3px",
                          }}
                        />
                        Regresar
                      </a>
                    </Button>
                    <Button variant="primary" type="submit">
                      <a>
                        <FontAwesomeIcon
                          icon={faCheckDouble}
                          style={{
                            paddingRight: "3px",
                          }}
                        />
                        Completar
                      </a>
                    </Button>
                    {user?.role === "Administrador" && (
                      <>
                        <div className="vr" />
                        <span>Exportar a </span>
                        <Button
                          variant="info"
                          size="sm"
                          className="d-flex align-items-center"
                          title="Descargar PDF"
                          onClick={handleDownloadPdf}
                        >
                          <PdfIcon />
                          <span className="ms-1">PDF</span>
                        </Button>
                        <Button
                          variant="info"
                          size="sm"
                          className="d-flex align-items-center"
                          title="Descargar Excel"
                          onClick={handleDownloadExcel}
                        >
                          <ExcelIcon />
                          <span className="ms-1">Excel</span>
                        </Button>
                      </>
                    )}
                  </Stack>
                </Card.Footer>
              </Card>
            </Row>
          </Form>
        </Container>
      </div>
    </>
  );
};

export default QuestionarySummary;
